import { useState, useMemo, useRef } from "react";
import Table from "views/merchandise/tables/BulkActionsTable";
import useCustomTableFilters from "hooks/useCustomTableFilters";
import { useAppSelector } from "redux/hooks";
import useDSPProductData from "./useDSPProductData";
import ExternalProductLink from "components/custom/merchandise/ExternalProductLink";
import TitleLink from "components/core/basic/TitleLink";
import NoImage from "assets/images/utility/no_image.png";
import getURLPrefix from "utils/getUrlPrefix";
import { buildTextColumn } from "views/merchandise/columns/buildTextColumn";
import { snakeCaseToSentenceCase, toTitleCase } from "utils/formatText";
import { DSP_PRODUCT_CONVERSION_TYPE_FILTER } from "./dspLifecycleFilters";
import { Badge } from "react-bootstrap";
import DSPProductExpander from "./ProductExpander";
import Switch from "react-bootstrap-switch";

const DSPProductsTable = ({ adGroupId, featuredAsinsFilter = false }) => {
  const [showParentAsins, setShowParentAsins] = useState(false);

  const marketPlace = useAppSelector((state) => state.marketPlace);
  const tableRef = useRef();
  const TABLE_ID = "dspProductsTable";
  const filterProps = useCustomTableFilters(TABLE_ID);

  const ADDITIONAL_FILTERS = [DSP_PRODUCT_CONVERSION_TYPE_FILTER];

  const {
    data: dspProductStatsData,
    metadata: dspProductMetadata,
    isLoading,
  } = useDSPProductData(
    "asin,conversion_type",
    true,
    featuredAsinsFilter ?
      {
        line_item_id: adGroupId,
        report_granularity: showParentAsins ? "Parent ASIN level" : "ASIN level",
        featured_asin: "Y",
      }
    : {
        line_item_id: adGroupId,
        report_granularity: showParentAsins ? "Parent ASIN level" : "ASIN level",
      },
  );

  const dspProductTableData = useMemo(() => {
    return dspProductStatsData
      .map((row) => {
        const metadata = dspProductMetadata?.find((meta) => meta.asin === row.asin);
        return { ...row, ...metadata };
      })
      ?.filter((row) => row.asin?.toLowerCase() !== "unallocated");
  }, [dspProductStatsData, dspProductMetadata]);

  const columnSpecs = useMemo(
    () => [
      buildTextColumn("conversion_type", "Conversion Type", {
        formatter: (v) => {
          return toTitleCase(snakeCaseToSentenceCase(v));
        },
        width: 120,
        hideFilter: true,
      }),
      {
        key: "dsp_total_detail_page_views",
        compareKey: "dsp_total_detail_page_views_percent",
      },
      {
        key: "dsp_new_to_brand_detail_page_views",
        compareKey: "dsp_new_to_brand_detail_page_views_percent",
      },
      {
        key: "dsp_total_add_to_cart",
        compareKey: "dsp_total_add_to_cart_percent",
      },
      {
        key: "dsp_total_purchases",
        compareKey: "dsp_total_purchases_percent",
      },

      {
        key: "dsp_total_new_to_brand_dpvs",
        compareKey: "dsp_total_new_to_brand_dpvs_percent",
      },
      {
        key: "dsp_total_units",
        compareKey: "dsp_total_units_percent",
      },
    ],
    [],
  );
  return (
    <Table
      marketPlace={marketPlace}
      tableRef={tableRef}
      tableId={TABLE_ID}
      isExpandable={true}
      isSelectable={false}
      titleCol={{
        id: "asin",
        isStatic: true,
        Header: "ASIN",
        accessor: "text",
        format: "text",
        style: { width: "300px" },
        width: 450,
        Cell: ({ row }) => {
          const { asin, product_name, report_granularity } = row?._original ?? {};

          const URL_PREFIX = getURLPrefix();
          const image = `${URL_PREFIX}/api/productimage/?asin=${asin}`;

          return (
            <div style={{ display: "flex", fontWeight: "500", fontSize: "1.5rem" }}>
              <div
                style={{
                  userSelect: "none",
                  WebkitUserSelect: "none",
                  flexShrink: 0,
                  width: 50,
                  height: 50,
                  marginRight: "10px",
                  background: `url(${image}) no-repeat center / contain, url("${NoImage}") no-repeat center / contain`,
                }}
                draggable="false"
              />
              <div className="overflow-ellipsis d-flex flex-column">
                <TitleLink
                  style={{
                    textDecoration: "none",
                    fontWeight: "500",
                    fontSize: "1.5rem",
                    cursor: "pointer",
                  }}
                  title={product_name}
                  to={null}
                />

                <div>
                  <i
                    className="pe-3"
                    style={{
                      fontWeight: "light",
                      color: "#666",
                      fontSize: "1rem",
                    }}
                  >
                    {asin}
                  </i>
                  <ExternalProductLink asin={asin} />
                  {report_granularity === "Parent ASIN level" && (
                    <Badge
                      className="badge bg-trellis-purple"
                      pill
                      style={{
                        borderRadius: "5px",
                        marginLeft: "1rem",
                      }}
                    >
                      Parent ASIN
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          );
        },
        Filter: () => null,
      }}
      data={dspProductTableData}
      columnSpecs={columnSpecs}
      excludedFilterColumns={["conversion_type"]}
      additionalFilters={
        featuredAsinsFilter ? null : (
          <div className="d-flex align-items-center gap-3 mt-2 me-5">
            <span>Child ASINs</span>
            <Switch
              onText=""
              offText=""
              value={showParentAsins}
              onColor="#FFFFFF"
              onChange={() => {
                setShowParentAsins(!showParentAsins);
              }}
            />
            <span>Parents ASINs</span>
          </div>
        )
      }
      additionalFilterOptions={ADDITIONAL_FILTERS}
      getSearchCriteria={(row) => {
        const { asin, product_name, conversion_type } = row ?? {};
        return `${asin} ${product_name} ${conversion_type}`;
      }}
      tableRowDescription={"Products"}
      defaultSorted={[{ id: "dsp_total_detail_page_views", desc: true }]}
      isLoading={isLoading}
      filterWidget={true}
      hideFilterToggleIcon={true}
      hideResetFiltersIcon={false}
      {...filterProps}
      handleBulkChanges={() => {}}
      minRows={1}
      selectionKey={"id"}
      ExpandedContent={({ row }) => <DSPProductExpander row={row.row} expanderFilters={null} />}
    />
  );
};

export default DSPProductsTable;
